import { Pill, type PillTone } from "@/components/ui/pill";
import type { LineStatus, MachineStatus } from "@opus-dt/shared-types";
import { getLineStatusLabel, getMachineStatusFriendlyLabel } from "@/lib/display-labels";

type AnyStatus = LineStatus | MachineStatus;
type EffectiveStatus = "CLEAN" | "DEGRADED" | "ANOMALOUS" | "IDLE";

const TONE_BY_STATUS: Record<string, PillTone> = {
  RUNNING: "success",
  STOPPED: "danger",
  PARTIAL: "warning",
  IDLE: "neutral",
  NODATA: "neutral",
};

const EFFECTIVE: Record<EffectiveStatus, { tone: PillTone; label: string; pulse: boolean }> = {
  CLEAN: { tone: "success", label: "Em produção", pulse: true },
  DEGRADED: { tone: "warning", label: "Em produção", pulse: true },
  ANOMALOUS: { tone: "danger", label: "Em produção", pulse: true },
  IDLE: { tone: "neutral", label: "Ociosa", pulse: false },
};

interface Props {
  status: AnyStatus;
  effectiveStatus?: EffectiveStatus;
  kind?: "line" | "machine";
  size?: "sm" | "md";
  pulse?: boolean;
  className?: string;
}

export function StatusBadge({
  status,
  effectiveStatus,
  kind = "line",
  pulse = true,
  className,
}: Props) {
  const useEffective = kind === "line" && effectiveStatus !== undefined;
  const label = useEffective
    ? EFFECTIVE[effectiveStatus].label
    : kind === "machine"
      ? getMachineStatusFriendlyLabel(status as MachineStatus)
      : getLineStatusLabel(status as LineStatus);
  const tone = useEffective
    ? EFFECTIVE[effectiveStatus].tone
    : (TONE_BY_STATUS[status] ?? "neutral");
  const shouldPulse = useEffective
    ? pulse && EFFECTIVE[effectiveStatus].pulse
    : pulse && (status === "RUNNING" || status === "STOPPED");
  return (
    <Pill
      tone={tone}
      withDot
      pulse={shouldPulse}
      aria-label={`Status: ${label}`}
      className={`uppercase ${className ?? ""}`}
    >
      {label}
    </Pill>
  );
}
